/* @flow */

import mousetrap from 'mousetrap';
import { PAGINA_INICIAL } from '../utils/constants';
import { focarLinha } from './utils';
import { type State, type GetState } from '../preact-app/types';

type Config = {
    namespace: string,
    pesquisar: Function,
    getGrid(State): Object,
};

const grid = ({
    namespace,
    pesquisar,
    getGrid,
}: Config) => {
    const types = {
        ALTERAR_ITENS: `${namespace}/grid/ALTERAR_ITENS`,
        PROXIMA_PAGINA: `${namespace}/grid/PROXIMA_PAGINA`,
        VOLTAR_PAGINA: `${namespace}/grid/VOLTAR_PAGINA`,
        ALTERAR_ORDEM: `${namespace}/grid/ALTERAR_ORDEM`,
        ALTERAR_QUANTIDADE: `${namespace}/grid/ALTERAR_QUANTIDADE`,
    };

    const getPodeVoltarPagina = (state: Object) => state.pagina > PAGINA_INICIAL;
    const getPodeAvancarPagina = (state: Object) => (
        state.itens.length > 0 && state.itens.length === state.quantidade
    );

    function reducer(state: Object, action: Object) {
        switch (action.type) {
        case types.ALTERAR_ITENS:
            return {
                ...state,
                itens: action.itens,
                permitirProximaPagina: (
                    action.itens.length > 0 && action.itens.length === state.quantidade
                ),
            };
        case types.PROXIMA_PAGINA:
            return {
                ...state,
                pagina: state.pagina + 1,
            };
        case types.VOLTAR_PAGINA:
            return !getPodeVoltarPagina(state) ? state : ({
                ...state,
                pagina: state.pagina - 1,
            });
        case types.ALTERAR_ORDEM:
            return {
                ...state,
                ordenacao: action.ordenacao,
            };
        case types.ALTERAR_QUANTIDADE:
            return {
                ...state,
                quantidade: action.quantidade,
                pagina: PAGINA_INICIAL,
            };
        default:
            return state;
        }
    }

    const alterarItens = (itens: Object[]) => ({
        type: types.ALTERAR_ITENS,
        itens,
    });

    const alterarOrdem = (ordenacao: string) => (dispatch: Function) => {
        dispatch({
            type: types.ALTERAR_ORDEM,
            ordenacao,
        });
        return dispatch(pesquisar());
    };

    const alterarQuantidade = (quantidade: number) => (dispatch: Function) => {
        dispatch({
            type: types.ALTERAR_QUANTIDADE,
            quantidade,
        });
        return dispatch(pesquisar());
    };

    const proximaPagina = () => (dispatch: Function) => {
        dispatch({
            type: types.PROXIMA_PAGINA,
        });
        return dispatch(pesquisar());
    };

    const voltarPagina = () => (dispatch: Function) => {
        dispatch({
            type: types.VOLTAR_PAGINA,
        });
        return dispatch(pesquisar());
    };

    const atalhos = (dispatch: Function, getState: GetState) => {
        mousetrap.bind('down', (e) => {
            const linha = document.activeElement;
            if (!(linha instanceof HTMLTableRowElement)) {
                return;
            }

            const tableBody = linha.parentElement;
            if (!(tableBody instanceof HTMLTableSectionElement)) {
                return;
            }

            if (linha.rowIndex === tableBody.rows.length
                && getPodeAvancarPagina(getGrid(getState()))
            ) {
                e.preventDefault();
                dispatch(proximaPagina()).then(() => {
                    const primeiraLinha = tableBody.rows[0];
                    if (primeiraLinha) {
                        primeiraLinha.focus();
                    }
                });
                return;
            }

            const proxima = tableBody.rows[linha.rowIndex];
            if (proxima) {
                e.preventDefault();
                proxima.focus();
            }
        });

        mousetrap.bind('up', (e) => {
            const linha = document.activeElement;
            if (!(linha instanceof HTMLTableRowElement)) {
                return;
            }

            const tableBody = linha.parentElement;
            if (!(tableBody instanceof HTMLTableSectionElement)) {
                return;
            }

            if (linha.rowIndex === 1 && getPodeVoltarPagina(getGrid(getState()))) {
                e.preventDefault();
                dispatch(voltarPagina()).then(() => {
                    const ultimaLinha = tableBody.rows[tableBody.rows.length - 1];
                    if (ultimaLinha) {
                        ultimaLinha.focus();
                    }
                });
                return;
            }

            const anterior = tableBody.rows[linha.rowIndex - 2];
            if (anterior) {
                e.preventDefault();
                anterior.focus();
            }
        });

        mousetrap.bind('left', () => {
            if (!getPodeVoltarPagina(getGrid(getState()))) {
                return;
            }
            const { activeElement } = document;
            if (!(activeElement instanceof HTMLTableRowElement)) {
                return;
            }
            const tableBody = activeElement.parentElement;
            if (!(tableBody instanceof HTMLTableSectionElement)) {
                return;
            }
            const index = activeElement.rowIndex - 1;

            dispatch(voltarPagina()).then(() => {
                tableBody.rows[index].focus();
            });
        });

        mousetrap.bind('right', () => {
            if (!getPodeAvancarPagina(getGrid(getState()))) {
                return;
            }
            const { activeElement } = document;
            if (!(activeElement instanceof HTMLTableRowElement)) {
                return;
            }
            const tableBody = activeElement.parentElement;
            if (!(tableBody instanceof HTMLTableSectionElement)) {
                return;
            }
            const index = activeElement.rowIndex - 1;

            dispatch(proximaPagina()).then(() => {
                focarLinha(tableBody.rows[index] || tableBody.rows[tableBody.rows.length - 1]);
            });
        });
    };

    const pesquisarSetAtalhos = () => (dispatch: Function, getState: GetState) => {
        dispatch(pesquisar());

        atalhos(dispatch, getState);
    };

    return {
        types,
        actions: {
            alterarItens,
            proximaPagina,
            alterarQuantidade,
            voltarPagina,
            alterarOrdem,
            pesquisar,
            pesquisarSetAtalhos,
        },
        reducer,
    };
};

export default grid;
